import { TagDTO, TagEntity } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { Path, useForm } from 'react-hook-form';
import { MultiValue } from 'react-select';
import { z } from 'zod';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Form, FormFieldItem } from '@/components/ui/form';
import { AsyncCreatableMultiSelect } from '@/components/ui/multi-select';
import { ApiService } from '@/services/api-v2';

const formSchema = z.object({
  tags: z.array(z.object({ label: z.string(), value: z.string() })),
});

function BulkUpdateCheckTagsDialog({
  open,
  onOpenChange,
  callback,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callback: (tags: string[], resetForm: () => void) => void;
  loading: boolean;
}) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tags: [],
    },
  });

  function handleSubmission(data: z.infer<typeof formSchema>) {
    const tags = data.tags.map((tag) => tag.value);
    callback(tags, () => form.reset());
  }

  function handleOpenChange(open: boolean) {
    onOpenChange(open);
    if (!open) {
      form.reset();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex flex-col gap-[10px]">
        <DialogTitle>Edit Schedule Check Tags</DialogTitle>

        <DialogDescription>Bulk editing will change the tags of all the schedule checks you selected.</DialogDescription>

        <Form {...form}>
          <form className="mt-2 flex h-full flex-col" onSubmit={form.handleSubmit(handleSubmission)}>
            <FormFieldItem<z.infer<typeof formSchema>, Path<z.infer<typeof formSchema>>> control={form.control} name="tags" label="Tags" labelSize="small">
              {({ value, onChange }) => (
                <AsyncCreatableMultiSelect<{ label: string; value: string }>
                  cacheOptions
                  defaultOptions
                  loadOptions={async (inputValue: string) => {
                    const { data } = await (
                      await ApiService.GetTags({
                        limit: 500,
                        filters: {
                          fields: { entity: TagEntity.Check },
                          search: inputValue,
                        },
                      })
                    ).json();

                    return data?.map((tag: TagDTO) => ({ label: tag.tag, value: tag.tag })) ?? [];
                  }}
                  placeholder="Type new tags or select from list..."
                  value={value as { label: string; value: string }[]}
                  onChange={(newValue: MultiValue<{ label: string; value: string }>) => {
                    onChange(newValue ? [...newValue] : []);
                  }}
                />
              )}
            </FormFieldItem>

            <div className="mt-4">
              <Button variant="success" type="submit" disabled={loading}>
                Save
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export { BulkUpdateCheckTagsDialog };
